import { useState, useEffect } from "react";
import { API } from "../../App";
import { Award, Star, TrendingUp, Gift, Zap } from "lucide-react";
import axios from "axios";
import { useTranslation } from "react-i18next";

export default function CustomerLoyalty() {
  const { t } = useTranslation();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/loyalty/profile`)
      .then(({ data }) => setProfile(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        {[...Array(3)].map((_, i) => <div key={i} className="animate-pulse bg-muted h-32 rounded-xl" />)}
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="text-center py-12 bg-card rounded-xl border border-border">
        <Award className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground">Unable to load loyalty profile</p>
      </div>
    );
  }

  const tierColors = {
    Bronze: "from-amber-700 to-amber-500",
    Silver: "from-gray-400 to-gray-300",
    Gold: "from-yellow-500 to-yellow-300",
    Platinum: "from-purple-600 to-indigo-400",
  };

  const progressPct = profile.next_tier
    ? Math.min(((profile.total_points - (profile.tiers?.find(t => t.name === profile.tier)?.min_points || 0)) / profile.points_to_next_tier) * 100, 100)
    : 100;

  return (
    <div data-testid="customer-loyalty-page">
      <h1 className="text-2xl font-medium text-foreground mb-6">{t("customer_loyalty:title")}</h1>

      {/* Tier Card */}
      <div className={`rounded-xl p-6 mb-6 text-white bg-gradient-to-br ${tierColors[profile.tier] || tierColors.Bronze}`} data-testid="loyalty-tier-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Award className="h-8 w-8" />
            <div>
              <p className="text-lg font-semibold">{profile.tier} Member</p>
              <p className="text-sm opacity-80">{profile.tier_multiplier}x point multiplier</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold">{profile.total_points}</p>
            <p className="text-sm opacity-80">total points</p>
          </div>
        </div>

        {profile.next_tier && (
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span>{profile.tier}</span>
              <span>{profile.next_tier.name} ({profile.points_to_next_tier} pts needed)</span>
            </div>
            <div className="w-full bg-white/20 rounded-full h-2">
              <div className="bg-white rounded-full h-2 transition-all" style={{ width: `${progressPct}%` }} />
            </div>
          </div>
        )}
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-card rounded-xl border border-border p-4 text-center" data-testid="loyalty-available-points">
          <Gift className="h-5 w-5 mx-auto text-primary mb-1" />
          <p className="text-xl font-semibold">{profile.available_points}</p>
          <p className="text-xs text-muted-foreground">{t("customer_loyalty:available")}</p>
        </div>
        <div className="bg-card rounded-xl border border-border p-4 text-center" data-testid="loyalty-bookings">
          <Zap className="h-5 w-5 mx-auto text-primary mb-1" />
          <p className="text-xl font-semibold">{profile.lifetime_bookings}</p>
          <p className="text-xs text-muted-foreground">{t("booking:title")}</p>
        </div>
        <div className="bg-card rounded-xl border border-border p-4 text-center" data-testid="loyalty-reviews">
          <Star className="h-5 w-5 mx-auto text-primary mb-1" />
          <p className="text-xl font-semibold">{profile.lifetime_reviews}</p>
          <p className="text-xs text-muted-foreground">Reviews</p>
        </div>
      </div>

      {/* Tiers Overview */}
      <div className="bg-card rounded-xl border border-border p-6 mb-6" data-testid="loyalty-tiers-overview">
        <h2 className="font-medium text-foreground mb-4">Tier Levels</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {(profile.tiers || []).map((t) => {
            const isActive = t.name === profile.tier;
            return (
              <div key={t.name} className={`p-3 rounded-lg border text-center ${isActive ? "border-primary bg-primary/5" : "border-border"}`}>
                <p className={`font-medium text-sm ${isActive ? "text-primary" : "text-foreground"}`}>{t.name}</p>
                <p className="text-xs text-muted-foreground">{t.min_points}+ pts</p>
                <p className="text-xs text-muted-foreground">{t.multiplier}x multiplier</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* How to Earn */}
      <div className="bg-card rounded-xl border border-border p-6 mb-6">
        <h2 className="font-medium text-foreground mb-4">How to Earn Points</h2>
        <div className="space-y-3">
          <div className="flex items-center gap-3 text-sm">
            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center shrink-0"><Zap className="h-4 w-4 text-primary" /></div>
            <span className="flex-1">Complete a booking</span>
            <span className="font-medium">50 pts</span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center shrink-0"><Star className="h-4 w-4 text-primary" /></div>
            <span className="flex-1">Submit a review</span>
            <span className="font-medium">25 pts</span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center shrink-0"><TrendingUp className="h-4 w-4 text-primary" /></div>
            <span className="flex-1">Per guest in booking</span>
            <span className="font-medium">10 pts</span>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      {profile.recent_activity?.length > 0 && (
        <div className="bg-card rounded-xl border border-border p-6" data-testid="loyalty-activity">
          <h2 className="font-medium text-foreground mb-4">Recent Activity</h2>
          <div className="space-y-2">
            {profile.recent_activity.map((a, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg text-sm">
                <div>
                  <span className="font-medium">{a.description}</span>
                  <span className="text-xs text-muted-foreground ml-2">{a.created_at?.split("T")[0]}</span>
                </div>
                <span className="text-primary font-medium">+{a.points}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
